import { yupResolver } from "@hookform/resolvers/yup";
import { i18n } from "@lingui/core";
import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import * as yup from "yup";

import { CheckBox, Link } from "@whoosh/design-system/src/components/controls";
import { Input } from "@whoosh/design-system/src/components/controls/Input";
import { PhoneInput } from "@whoosh/design-system/src/components/controls/PhoneInput";
import { buildFullPhoneNumber } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/buildPhoneNumber";
import { getCountryCodeById } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/countryCodes";
import { Loader } from "@whoosh/design-system/src/components/display";
import { TrashIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";
import {
  getImageInput,
  getImageRenderingKey,
} from "@whoosh/utilities/src/Cloudinary";
import { isPhoneValid } from "@whoosh/utilities/src/phone";

import { ProfileUpdateFormQueryRendererQuery } from "./__generated__/ProfileUpdateFormQueryRendererQuery.graphql";
import { UpdateMemberProfileInput } from "./__generated__/ProfileUpdateFormQueryRendererQueryMutation.graphql";
import { EditPhoto } from "./components";
import usePickImage from "./components/EditPhoto/hooks/usePickImage";
import SubmitButton from "./components/SubmitButton";
import { useUpdateMemberProfile } from "./hooks/useUpdateMemberProfile";
import { ERROR_MESSAGES, FIELD_MAP, useFormProps } from "./types";

const nameRegExp = /^[\p{L}\p{M}'-.,]+(?: [\p{L}\p{M}'-.,]+)*$/u;

export const ProfileUpdateFormQueryRenderer = ({
  clubRef,
  isOnboarding,
  submitNavigate,
  onDelete,
}: {
  clubRef: PreloadedQuery<ProfileUpdateFormQueryRendererQuery>;
  isOnboarding?: boolean;
  submitNavigate: () => void;
  onDelete?: () => void;
}) => {
  const data = usePreloadedQuery(
    ProfileUpdateFormQueryRendererQueryString,
    clubRef
  );

  const firstNameErrorMessage = t`First name is a required field`;
  const lastNameErrorMessage = t`Last name is a required field`;

  const schema = yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .matches(nameRegExp, firstNameErrorMessage)
      .required(firstNameErrorMessage),
    lastName: yup
      .string()
      .trim()
      .matches(nameRegExp, lastNameErrorMessage)
      .required(lastNameErrorMessage),
    email: yup.string().trim().email().nullable(),
  });

  const member = data.session?.currentClientProfile?.member;
  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
  } = useForm<useFormProps>({
    resolver: yupResolver(schema),
    mode: "onBlur",
    defaultValues: {
      firstName: `${member?.firstName || ""}`,
      lastName: `${member?.lastName || ""}`,
      email: member?.email,
    },
  });

  const [tosAgreed, setTosAgreed] = useState(false);

  const defaultCountryCodeId =
    data.session?.currentClientProfile?.member?.club?.defaultCountryCode ||
    "US";
  const defaultCountryCode = getCountryCodeById(defaultCountryCodeId);

  const [currentCountryCode, setCurrentCountryCode] = useState(
    data.session?.currentClientProfile?.member?.phoneNumber?.countryCode.toString() ||
      defaultCountryCode?.code?.toString() ||
      "1"
  );
  const [phoneNumber, setPhoneNumber] = useState(
    data.session?.currentClientProfile?.member?.phoneNumber?.nationalNumber ||
      ""
  );

  const phoneHasErrors = !!(
    phoneNumber && !isPhoneValid(phoneNumber, currentCountryCode)
  );

  const phoneNumberPlaceholder = useMemo(() => {
    return currentCountryCode === "1" ? "(123) 456-7890" : "020 7123 4567";
  }, [currentCountryCode]);

  // Not specifying this makes it register as not mobile at MD BP ¯\_(ツ)_/¯
  const isMobileLayout = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    lg: false,
    xl: false,
  });

  useEffect(() => {
    if (!isOnboarding) {
      setTosAgreed(true);
    }
  }, [isOnboarding]);

  const onCompletedErrorCustom = (errors: any) => {
    errors.forEach((error: any) => {
      const name = FIELD_MAP[`${error?.field[0]}`];
      const message = ERROR_MESSAGES[name];
      setError(name, { message: message ? i18n._(message) : undefined });
    });
    return [];
  };

  const [updateMemberProfile, isUpdating] = useUpdateMemberProfile(
    submitNavigate,
    onCompletedErrorCustom
  );

  const onSubmit = (formData: useFormProps) => {
    const trimmedFormData = {
      ...formData,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email?.trim(),
    };

    const { email, ...rest } = trimmedFormData;

    // we need to send up undefined if they did not input a phone number
    const fullPhoneNumber = buildFullPhoneNumber(
      currentCountryCode,
      phoneNumber
    );

    const input: UpdateMemberProfileInput = {
      ...rest,
      phoneNumber: fullPhoneNumber || undefined,
      memberId: member?.id || "",
    };

    if (imageObject || removedImage) {
      input.profileImage = removedImage
        ? null
        : imageObject
        ? getImageInput(imageObject)
        : null;
    }

    updateMemberProfile(input);
  };

  const {
    pickImage,
    removedImage,
    setRemovedImage,
    imageObject,
    setImageObject,
    isImageUploading,
  } = usePickImage(member?.club?.id);

  const hasProfileImage = removedImage
    ? false
    : getImageRenderingKey(imageObject) ||
      (member?.profileImage?.publicId && !isImageUploading);

  const isColumnLayout = isMobileLayout || !!isOnboarding;

  return (
    <>
      {isUpdating ? <Loader /> : null}
      <Box>
        {isOnboarding ? (
          <>
            <VStack px={6}>
              <Text mt={8} mb={4} variant="mainTitle2" color="text-primary">
                {t`Welcome`} {`${member?.firstName || ""}!`}
              </Text>
              <Text
                variant="mainBaseMedium"
                color="text-secondary"
                mt={1}
              >{t`Update your contact information to provide you with the best experience.`}</Text>
            </VStack>
          </>
        ) : null}
        <Box px={isOnboarding ? 6 : 0}>
          <Box my={6}>
            <EditPhoto
              hasProfileImage={!!hasProfileImage}
              publicId={
                removedImage
                  ? undefined
                  : getImageRenderingKey(imageObject) ??
                    member?.profileImage?.publicId ??
                    undefined
              }
              memberFirstName={member?.firstName || ""}
              memberLastName={member?.lastName || ""}
              isImageUploading={isImageUploading}
              pickImage={pickImage}
              setRemovedImage={setRemovedImage}
              setImageObject={setImageObject}
            />
            <Box
              mt={10}
              mb={isColumnLayout ? 0 : 4}
              flexDirection={isColumnLayout ? "column" : "row"}
            >
              <HStack
                width={isColumnLayout ? "100%" : "30%"}
                mb={isColumnLayout ? 4 : 0}
                mr={isColumnLayout ? 0 : 4}
                gap={2}
              >
                <Box width={"50%"}>
                  <Controller
                    control={control}
                    name="firstName"
                    render={({ field }) => {
                      return (
                        <Input
                          testID="firstName"
                          label={t`First Name`}
                          name="firstName"
                          size="large"
                          placeholder={t`First Name`}
                          value={field.value}
                          onChange={(val) => field.onChange(val)}
                          hasError={!!errors.firstName}
                          subMessage={errors.firstName?.message ?? ""}
                        />
                      );
                    }}
                  />
                </Box>
                <Box width={"50%"}>
                  <Controller
                    control={control}
                    name="lastName"
                    render={({ field }) => {
                      return (
                        <Input
                          testID="lastName"
                          label={t`Last Name`}
                          name="lastName"
                          size="large"
                          placeholder={t`Last Name`}
                          value={field.value}
                          onChange={(val) => field.onChange(val)}
                          hasError={!!errors.lastName}
                          subMessage={errors.lastName?.message ?? ""}
                        />
                      );
                    }}
                  />
                </Box>
              </HStack>
              <Box
                width={isColumnLayout ? "100%" : "30%"}
                mb={isColumnLayout ? 4 : 0}
                mr={isColumnLayout ? 0 : 4}
              >
                <Controller
                  control={control}
                  name={"email"}
                  render={({ field }) => {
                    return (
                      <Input
                        testID="email"
                        label={t`Email`}
                        name="email"
                        size="large"
                        placeholder={t`youremail@gmail.com`}
                        value={field.value ?? ""}
                        onChange={(val) => field.onChange(val)}
                        hasError={!!errors.email}
                        subMessage={errors.email?.message ?? ""}
                      />
                    );
                  }}
                />
              </Box>

              <Box
                width={isColumnLayout ? "100%" : "30%"}
                mb={isColumnLayout ? 4 : 0}
                mr={isColumnLayout ? 0 : 4}
              >
                <Box mb={2}>
                  <Text variant="mainTiny" color="text-secondary">
                    {t`Phone Number`}
                  </Text>
                </Box>
                <PhoneInput
                  hasError={phoneHasErrors}
                  placeholder={phoneNumberPlaceholder}
                  nationalNumber={phoneNumber}
                  defaultCountryId={currentCountryCode}
                  onChange={({ countryCode, nationalNumber }) => {
                    setCurrentCountryCode(countryCode);
                    setPhoneNumber(nationalNumber);
                  }}
                  onCountryCodeChange={(code) => {
                    setCurrentCountryCode(code);
                  }}
                  name={"phoneNumber"}
                />
              </Box>
            </Box>
            <VStack width={isColumnLayout ? "100%" : "30%"}>
              {!isOnboarding ? (
                <Box mt={8}>
                  <SubmitButton
                    tosAgreed={tosAgreed}
                    isImageUploading={isImageUploading}
                    handleSubmit={handleSubmit}
                    onSubmit={onSubmit}
                  />
                </Box>
              ) : null}

              {isOnboarding ? null : onDelete ? (
                <VStack mt={10} mb={{ base: "-1", lg: 0 }}>
                  <Text variant="mainHeading" mb={4}>
                    {t`Delete Account`}
                  </Text>
                  <Text variant="mainBaseMedium" color="grayscale-800">
                    {t`Are you sure you want to permanently delete your account?
                      You will lose all preferences.`}
                  </Text>
                  <Pressable
                    mt={8}
                    px={5}
                    py={5}
                    style={{ borderRadius: 8 }}
                    backgroundColor="status-error-500"
                    onPress={() => onDelete()}
                  >
                    <HStack gap={3} justifyContent="center">
                      <TrashIcon color="status-error-500" />
                      <Text variant="mainBaseSemiBold" color="text-error">
                        {t`Delete Account`}
                      </Text>
                    </HStack>
                  </Pressable>
                </VStack>
              ) : null}
            </VStack>
          </Box>
        </Box>

        {isOnboarding ? (
          <Box px={6}>
            <VStack gap={4}>
              <HStack mt={6} mb={4} gap={1} alignItems="center">
                <CheckBox
                  ariaLabel={t`Agree to terms and policies`}
                  isChecked={tosAgreed}
                  onPress={() => setTosAgreed(!tosAgreed)}
                  testID="terms-agreement-checkbox"
                />
                <HStack pl={2} flexWrap={"wrap"} flex={1}>
                  <Pressable onPress={() => setTosAgreed(!tosAgreed)}>
                    <Text variant="mainBaseMedium">
                      {t`By checking this box you agree to the following`}{" "}
                    </Text>
                  </Pressable>
                  <Link
                    href="https://www.whoosh.io/privacy"
                    target="_blank"
                    iconType="external"
                  >
                    <Text variant="mainBaseSemiBold">{t`Privacy Policy`}</Text>
                  </Link>
                  <Pressable onPress={() => setTosAgreed(!tosAgreed)}>
                    <Text variant="mainBaseMedium"> {t`and`} </Text>
                  </Pressable>
                  <Link
                    href="https://www.whoosh.io/terms"
                    target="_blank"
                    iconType="external"
                  >
                    <Text variant="mainBaseSemiBold">{t`Terms of Service`}</Text>
                  </Link>
                </HStack>
              </HStack>
              <SubmitButton
                tosAgreed={tosAgreed}
                isImageUploading={isImageUploading}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
              />
            </VStack>
          </Box>
        ) : null}
      </Box>
    </>
  );
};

export const ProfileUpdateFormQueryRendererQueryString = graphql`
  query ProfileUpdateFormQueryRendererQuery {
    session {
      currentClientProfile {
        member {
          club {
            id
            defaultCountryCode
          }
          ... on Member {
            id
            firstName
            lastName
            email
            phoneNumber {
              nationalNumber
              countryCode
            }
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
            memberGroups {
              id
            }
            departmentMemberProfiles(type: GOLF) {
              ... on GolfMemberProfile {
                department {
                  id
                  name
                  facilities {
                    edges {
                      node {
                        type
                        transportationTypes {
                          edges {
                            node {
                              id
                              code
                              enabled
                              memberVisible
                              customCode
                              name
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const ProfileUpdateFormGql = graphql`
  mutation ProfileUpdateFormQueryRendererQueryMutation(
    $input: UpdateMemberProfileInput!
  ) {
    responsePayload: updateMemberProfile(input: $input) {
      member {
        departmentMemberProfiles(type: GOLF) {
          ... on GolfMemberProfile {
            department {
              id
              name
            }
          }
        }
        id
        firstName
        lastName
        profileImage {
          # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
          publicId: renderingKey
        }
        phoneNumber {
          nationalNumber
        }
        user {
          memberProfiles {
            edges {
              node {
                id
                onboardedAt
              }
            }
          }
        }
      }
      success
      errors {
        code

        message
        field
      }
    }
  }
`;
